import { z } from "zod";
import { GuestPlanSeatingSchema } from "./seating-plan";
export const RsvpStatusSchema = z.enum(["pending", "accepted", "declined"]);
export const MealPreferenceSchema = z.enum([
    "standard",
    "vegetarian",
    "vegan",
    "gluten_free",
    "lactose_free",
    "child",
    "other",
]);
export const ImportSourceSchema = z.enum([
    "manual",
    "csv",
    "ai_image",
    "ai_pdf",
    "rsvp",
    "contacts",
]);
/**
 * Channel the couple used to hand off the RSVP link to a guest. Set when the
 * couple taps one of the channel buttons in the Send sheet; cleared by the
 * "Cofnij" path. Independent from `rsvpStatus` — delivery axis vs. response
 * axis.
 */
export const InvitationChannelSchema = z.enum(["whatsapp", "messenger", "sms", "email", "copy"]);
export const GuestGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
/**
 * Plus-one / companion. Shares the host's RSVP and attendance toggles —
 * if the host declines, the plus-one isn't there either. Has its own
 * meal preference because dietary needs commonly differ.
 *
 * When `guestId` is set, this companion is the same person as an existing
 * Guest doc — the UI hides that doc's standalone host row.
 */
export const PlusOneSchema = z.object({
    /** May be empty string for an anonymous companion — UI falls back to "Osoba towarzysząca". */
    firstName: z.string(),
    lastName: z.string().nullable().optional(),
    mealPreference: MealPreferenceSchema.nullable().optional(),
    guestId: z.string().nullable().optional(),
});
/**
 * One child, modelled per-name so placecards and seating can show them
 * individually. Age is optional; we don't gate behaviour on it but the UI
 * surfaces it for meal-planning context.
 *
 * When `guestId` is set, this child is the same person as an existing Guest
 * doc — the UI hides that doc's standalone host row.
 */
export const ChildSchema = z.object({
    id: z.string(),
    firstName: z.string().min(1),
    lastName: z.string().nullable().optional(),
    age: z.number().int().min(0).max(17).nullable().optional(),
    mealPreference: MealPreferenceSchema.nullable().optional(),
    guestId: z.string().nullable().optional(),
});
/** System guest role — only Pan Młody / Pani Młoda carry these values. */
export const WeddingCoupleRoleSchema = z.enum(["groom", "bride"]);
/**
 * Honorary witness role a guest may hold. Gendered because Polish names the
 * role itself by gender — `witnessM` = Świadek, `witnessF` = Świadkowa. A
 * wedding can have several; no uniqueness is enforced here.
 */
export const WitnessRoleSchema = z.enum(["witnessM", "witnessF"]);
/**
 * Accommodation payment mode for guests the couple is hosting.
 *
 * Only two modes exist:
 * - `full`   → couple covers the guest's full share of the assigned room.
 *              `roomPrice * nights / max(1, occupants)`. This is also the
 *              implicit default when no `accommodationPayment` record is
 *              present on an assigned guest. `coupleContribution` is not
 *              meaningful here and MUST NOT be present.
 * - `partial` → couple covers exactly `coupleContribution` in the wedding's
 *              currency for this guest; the guest pays the remainder
 *              themselves.
 *
 * Modelled as a discriminated union on `mode` so the per-mode field set is
 * enforced by the schema (no stray `coupleContribution` on a `full` doc).
 *
 * Guests who arrange their own accommodation are represented by NOT having
 * `accommodationRequired = true`. There is no "self" mode here — that
 * concept lives outside the accommodation flow entirely.
 */
export const AccommodationPaymentModeSchema = z.enum(["partial", "full"]);
export const AccommodationPaymentSchema = z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("full") }),
    z.object({
        mode: z.literal("partial"),
        /**
         * Amount the couple covers for this guest, in the wedding's currency
         * (see `wedding.settings.currency`). Optional / nullable so the input
         * field can be empty mid-edit before the user commits a number.
         */
        coupleContribution: z.number().nonnegative().nullable().optional(),
    }),
]);
export const GuestSchema = z.object({
    id: z.string(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    phone: z.string().nullable().optional(),
    groupId: z.string().nullable().optional(),
    rsvpStatus: RsvpStatusSchema.default("pending"),
    rsvpRespondedAt: z.string().datetime().nullable().optional(),
    /**
     * Secret, unguessable token behind this guest's public RSVP link.
     * Generated lazily the first time the couple shares the link; null until
     * then. The matching `/rsvpTokens/{token}` doc carries the form payload.
     */
    rsvpToken: z.string().nullable().default(null),
    /**
     * Write-proof field set by the public RSVP form to the token from the URL.
     * Firestore rules compare it against `rsvpToken` to authorise the
     * unauthenticated update — see firestore.rules.
     */
    rsvpSubmittedToken: z.string().nullable().optional(),
    /** ISO timestamp when the couple last handed off the RSVP link via the Send sheet. */
    invitationSentAt: z.string().datetime().nullable().optional(),
    /** Channel used the last time the link was handed off — see {@link InvitationChannelSchema}. */
    invitationChannel: InvitationChannelSchema.nullable().optional(),
    mealPreference: MealPreferenceSchema.nullable().optional(),
    attendingCeremony: z.boolean().default(true),
    attendingReception: z.boolean().default(true),
    attendingAfterParty: z.boolean().default(false),
    accommodationRequired: z.boolean().default(false),
    /**
     * How the couple pays for this guest's accommodation. Null/missing on an
     * assigned guest is treated as `{ mode: "full" }`. See
     * {@link AccommodationPaymentModeSchema}.
     */
    accommodationPayment: AccommodationPaymentSchema.nullable().optional(),
    /** @deprecated Use seatingByPlan[planId]. Kept for legacy migration reads. */
    tableId: z.string().nullable().optional(),
    /** @deprecated Use seatingByPlan[planId]. Kept for legacy migration reads. */
    seatNumber: z.number().int().nullable().optional(),
    /** Per seating-plan version assignments keyed by planId. */
    seatingByPlan: z.record(z.string(), GuestPlanSeatingSchema).optional(),
    /** Whether the guest may bring a plus-one — surfaced on the public RSVP form. */
    plusOneAllowed: z.boolean().default(false),
    plusOne: PlusOneSchema.nullable().default(null),
    children: z.array(ChildSchema).default([]),
    notes: z.string().nullable().optional(),
    importSource: ImportSourceSchema.default("manual"),
    /**
     * When set, this guest is one half of the wedding couple (groom/bride).
     * `partnerId` holds the id of the other system guest. Both carry each other's
     * id. Displayed as a peer couple unit with ring styling in the guest list.
     */
    partnerId: z.string().nullable().optional(),
    /** System role for the non-removable wedding couple guests. */
    weddingRole: WeddingCoupleRoleSchema.nullable().optional(),
    /** Honorary witness role (Świadek / Świadkowa) — see {@link WitnessRoleSchema}. */
    witnessRole: WitnessRoleSchema.nullable().optional(),
    deletedAt: z.string().datetime().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * Head count for one guest's invitation unit: host + companion + children.
 * Deliberately RSVP-agnostic — callers compose their own filter so the
 * same helper serves both "confirmed heads" (filter accepted) and
 * "seating capacity used" (anyone actually assigned to a chair, regardless
 * of RSVP).
 */
export function headCount(g) {
    const plusOne = g.plusOne?.firstName?.trim() ? 1 : 0;
    const children = (g.children ?? []).filter((c) => c.firstName?.trim()).length;
    return 1 + plusOne + children;
}
/**
 * Shape returned by the AI import pipeline before it lands in Firestore.
 * The wizard accumulates these in memory, lets the user toggle / edit
 * per row, then maps to GuestDraft on commit.
 *
 * Confidence is a UI hint, not a data point — drives the row's tag colour
 * in the preview table. Group is captured as raw text from the source;
 * the wizard's group-mapping step resolves it to a real GuestGroup id (or
 * creates a new group) just before persisting.
 */
export const ImportConfidenceSchema = z.enum(["high", "medium", "low"]);
export const ParsedGuestSchema = z.object({
    /** UUID — stable React key during edit. Not persisted. */
    id: z.string(),
    firstName: z.string().min(1),
    lastName: z.string().default(""),
    phone: z.string().nullable().optional(),
    /** Raw group name from the source — fuzzy-matched to GuestGroup on commit. */
    groupName: z.string().nullable().optional(),
    mealPreference: MealPreferenceSchema.nullable().optional(),
    rsvpStatus: RsvpStatusSchema.optional(),
    plusOneName: z.string().nullable().optional(),
    childrenNames: z.array(z.string()).default([]),
    notes: z.string().nullable().optional(),
    confidence: ImportConfidenceSchema.default("medium"),
    /** Set true → row will be persisted on commit. */
    include: z.boolean().default(true),
    /** Set by the wizard when an existing Guest matches firstName + lastName. */
    duplicateOf: z.string().nullable().optional(),
});
